import axios from 'axios';
import { newApiBaseUrl } from '../common/Function';

const DEVICE_ID_KEY = '@altrisa_web_device_id';
const SESSION_ID_KEY = '@altrisa_web_session_id';

// Obtener o generar un ID único persistente para este navegador
export const getOrCreateWebDeviceId = () => {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      const randomStr = Math.random().toString(36).substring(2, 12) + Math.random().toString(36).substring(2, 12);
      deviceId = `web_${Date.now()}_${randomStr}`;
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch (e) {
    return `web_fallback_${Date.now()}`;
  }
};

// Obtener o inicializar ID de sesión actual (se renueva si pasa más de 30 minutos)
export const getSessionId = () => {
  try {
    let session = sessionStorage.getItem(SESSION_ID_KEY);
    if (!session) {
      session = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      sessionStorage.setItem(SESSION_ID_KEY, session);
    }
    return session;
  } catch {
    return 'sess_fallback';
  }
};

// Cliente Axios dedicado para telemetría
const telemetryClient = axios.create({
  baseURL: newApiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Client-Type': 'web',
    'X-Device-Id': getOrCreateWebDeviceId(),
  },
  timeout: 5000,
});

// Adjuntar token si el usuario está autenticado
telemetryClient.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Device-Id'] = getOrCreateWebDeviceId();
  } catch (e) {
    // ignorar
  }
  return config;
});

let lastTrackedScreen = null;
let lastTrackedTime = 0;

export const telemetryService = {
  /**
   * Registrar una vista de pantalla en la Web.
   * Evita duplicados rápidos (dentro de 1.5s en la misma ruta).
   */
  async trackScreenView(screenName, routePath = window.location.hash || window.location.pathname, durationSeconds = null) {
    const now = Date.now();
    if (lastTrackedScreen === screenName && now - lastTrackedTime < 1500) {
      return;
    }
    lastTrackedScreen = screenName;
    lastTrackedTime = now;

    try {
      await telemetryClient.post('/telemetry/screen-view', {
        screen_name: screenName,
        route_path: routePath,
        client_type: 'web',
        device_id: getOrCreateWebDeviceId(),
        session_id: getSessionId(),
        duration_seconds: durationSeconds,
        metadata: {
          screen_resolution: `${window.innerWidth}x${window.innerHeight}`,
          url: window.location.href,
        },
      });
    } catch (error) {
      // La telemetría nunca debe romper la ejecución ni alertar al usuario
      console.debug('Error enviando telemetría web:', error?.message);
    }
  },

  /**
   * Obtener estadísticas de uso para el panel de administración
   */
  async getStats(days = 30) {
    try {
      const response = await telemetryClient.get(`/telemetry/stats?days=${days}`);
      return response.data?.data || null;
    } catch (error) {
      console.error('Error obteniendo estadísticas de telemetría:', error);
      throw error;
    }
  },
};

export default telemetryService;
